import {
  Button,
  DatePickerDropdown,
  Dialog,
  MenuItem,
  Select,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { ProductApi } from '@/utilities/api/ProductApi';
import { ProductExclusionApi } from '@/utilities/api/ProductExclusionApi';
import { ApiProductExclusion } from '@api/interfaces';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { Alert, Stack, useTheme } from '@mui/material';
import { ProductExclusionType } from '@shared/enums';
import {
  formatDateOnly,
  formatMediumDate,
  productExclusionPeriodsOverlap,
} from '@shared/utilities';
import i18next from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

interface ProductExclusionsModalProps {
  onClose: () => void,
  productId: string,
  productName: string,
}

type PendingChange =
  | { exclusion: ApiProductExclusion, kind: 'delete' }
  | {
    endDate: string | null,
    kind: 'create',
    startDate: string | null,
    type: ProductExclusionType,
  };

export const ProductExclusionsModal = ({
  onClose,
  productId,
  productName,
}: ProductExclusionsModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [isStartDateValid, setIsStartDateValid] = useState(true);
  const [isEndDateValid, setIsEndDateValid] = useState(true);
  const [type, setType] = useState(ProductExclusionType.ProgramPoints);
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const { data: exclusions, isFetching } = useQuery(
    [QueryKeys.GET_PRODUCT_EXCLUSIONS, productId],
    () => ProductExclusionApi.list(productId),
  );

  const affectedPeriod = pendingChange && (
    pendingChange.kind === 'delete'
      ? pendingChange.exclusion
      : pendingChange
  );

  const { data: impact } = useQuery(
    [QueryKeys.GET_PRODUCT_PURCHASE_IMPACT, productId, affectedPeriod],
    () => ProductApi.getPurchaseImpact(productId, {
      ...(affectedPeriod?.endDate && { endDate: affectedPeriod.endDate }),
      ...(affectedPeriod?.startDate && { startDate: affectedPeriod.startDate }),
    }),
    { enabled: !!affectedPeriod },
  );

  const { isLoading: isSaving, mutateAsync: applyChange } = useMutation(
    async (change: PendingChange) => {
      if (change.kind === 'delete') {
        return ProductExclusionApi.delete(productId, change.exclusion.id);
      }
      return ProductExclusionApi.create(productId, {
        endDate: change.endDate,
        startDate: change.startDate,
        type: change.type,
      });
    },
    {
      onError: (error) => {
        setPendingChange(null);
        setErrorMessage((error as Error).message ?? t($ => $.common.error.genericError));
      },
      onSuccess: () => {
        setPendingChange(null);
        setStartDate(undefined);
        setEndDate(undefined);
        void queryClient.invalidateQueries([QueryKeys.GET_PRODUCT_EXCLUSIONS, productId]);
        void queryClient.invalidateQueries(QueryKeys.GET_PRODUCT);
      },
    },
  );

  const requestChange = async (change: PendingChange) => {
    setErrorMessage('');
    const period = change.kind === 'delete' ? change.exclusion : change;
    const { purchaseCount } = await ProductApi.getPurchaseImpact(productId, {
      ...(period.endDate && { endDate: period.endDate }),
      ...(period.startDate && { startDate: period.startDate }),
    });
    if (purchaseCount) {
      setPendingChange(change);
      return;
    }
    await applyChange(change);
  };

  const describeDate = (value: string | null, fallback: string) => (
    value ? formatMediumDate(value, i18next) : fallback
  );

  const describeType = (exclusionType: ProductExclusionType) => (
    exclusionType === ProductExclusionType.EntireProgram
      ? t($ => $.products.exclusions.types.entireProgram)
      : t($ => $.products.exclusions.types.programPoints)
  );

  const onAdd = async () => {
    if (startDate && endDate && endDate <= startDate) {
      setErrorMessage(t($ => $.products.exclusions.errors.endBeforeStart));
      return;
    }

    const period = {
      endDate: endDate ? formatDateOnly(endDate) : null,
      startDate: startDate ? formatDateOnly(startDate) : null,
    };

    const overlapping = exclusions?.data.find((exclusion) => (
      productExclusionPeriodsOverlap(period, exclusion)
    ));
    if (overlapping) {
      setErrorMessage(t($ => $.products.exclusions.errors.overlapping, {
        end: describeDate(overlapping.endDate, t($ => $.products.exclusions.ongoing)),
        start: describeDate(overlapping.startDate, t($ => $.products.exclusions.fromBeginning)),
        type: describeType(overlapping.type),
      }));
      return;
    }

    await requestChange({ ...period, kind: 'create', type });
  };

  return (
    <>
      <Dialog
        acceptButton={(props) => (
          <Button
            {...props}
            disabled={!isStartDateValid || !isEndDateValid}
            loading={isSaving}
            onClick={() => void onAdd()}
            testID="exclusion-add"
            variant="contained"
          >
            {t($ => $.products.exclusions.add)}
          </Button>
        )}
        cancelButton={(props) => (
          <Button {...props} color="inherit" onClick={onClose} testID="exclusions-close">
            {t($ => $.common.actions.close)}
          </Button>
        )}
        maxWidth="md"
        onClose={onClose}
        open={!pendingChange}
        showCloseButton={false}
        testID="product-exclusions-modal"
        title={t($ => $.products.exclusions.modalTitle, { productName })}
      >
        <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
          {t($ => $.products.exclusions.description)}
        </Text>
        <VSpacer size="5" />
        {!isFetching && !exclusions?.data.length && (
          <Text category="body-medium" testID="exclusions-empty">
            {t($ => $.products.exclusions.none)}
          </Text>
        )}
        <Stack gap="12px">
          {exclusions?.data.map((exclusion) => (
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
              key={exclusion.id}
            >
              <Text category="body-medium" testID={`exclusion-row-${exclusion.id}`}>
                {t($ => $.products.exclusions.row, {
                  end: describeDate(exclusion.endDate, t($ => $.products.exclusions.ongoing)),
                  start: describeDate(
                    exclusion.startDate,
                    t($ => $.products.exclusions.fromBeginning),
                  ),
                  type: describeType(exclusion.type),
                })}
              </Text>
              <Button
                color="error"
                onClick={() => void requestChange({ exclusion, kind: 'delete' })}
                size="small"
                startIcon={<DeleteOutline />}
                testID={`exclusion-delete-${exclusion.id}`}
              >
                {t($ => $.products.exclusions.delete)}
              </Button>
            </Stack>
          ))}
        </Stack>
        <VSpacer size="6" />
        <Text category="title-small">{t($ => $.products.exclusions.addTitle)}</Text>
        <VSpacer size="3" />
        <Stack alignItems="flex-start" direction="row" flexWrap="wrap" gap="16px">
          <Select
            label={t($ => $.products.exclusions.type)}
            onChangeValue={(value) => setType(value as ProductExclusionType)}
            testID="exclusion-type"
            value={type}
          >
            <MenuItem
              testID="exclusion-type-program-points"
              value={ProductExclusionType.ProgramPoints}
            >
              {describeType(ProductExclusionType.ProgramPoints)}
            </MenuItem>
            <MenuItem
              testID="exclusion-type-entire-program"
              value={ProductExclusionType.EntireProgram}
            >
              {describeType(ProductExclusionType.EntireProgram)}
            </MenuItem>
          </Select>
          <DatePickerDropdown
            label={t($ => $.products.exclusions.startDate)}
            onChange={setStartDate}
            onValidityChange={setIsStartDateValid}
            testID="exclusion-start-date"
            value={startDate}
            width={220}
          />
          <DatePickerDropdown
            label={t($ => $.products.exclusions.endDate)}
            onChange={setEndDate}
            onValidityChange={setIsEndDateValid}
            testID="exclusion-end-date"
            value={endDate}
            width={220}
          />
        </Stack>
        <VSpacer size="2" />
        <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
          {t($ => $.products.exclusions.datesHint)}
        </Text>
        {!!errorMessage && (
          <>
            <VSpacer size="4" />
            <Alert color="error" icon={false}>{errorMessage}</Alert>
          </>
        )}
      </Dialog>
      {!!pendingChange && (
        <Dialog
          acceptButton={(props) => (
            <Button
              {...props}
              loading={isSaving}
              onClick={() => void applyChange(pendingChange)}
              testID="exclusion-confirm"
              variant="contained"
            >
              {t($ => $.products.exclusions.confirm)}
            </Button>
          )}
          cancelButton={(props) => (
            <Button
              {...props}
              color="inherit"
              onClick={() => setPendingChange(null)}
              testID="exclusion-confirm-cancel"
            >
              {t($ => $.common.actions.cancel)}
            </Button>
          )}
          maxWidth="xs"
          onClose={() => setPendingChange(null)}
          open
          showCloseButton={false}
          testID="product-exclusion-confirm-modal"
          title={t($ => $.products.exclusions.confirmTitle)}
        >
          <Text category="body-medium">
            {t($ => $.products.exclusions.confirmBody, {
              customers: t($ => $.products.exclusions.customerCount, {
                count: impact?.customerCount ?? 0,
              }),
              productName,
              purchases: t($ => $.products.exclusions.purchaseCount, {
                count: impact?.purchaseCount ?? 0,
              }),
            })}
          </Text>
        </Dialog>
      )}
    </>
  );
};
